import * as Sentry from '@sentry/react';

/**
 * Records a failure the code ALREADY expects, without opening a Sentry issue.
 *
 * Why it exists: the 2026-09-02 triage measured that 36% of the panel's weekly volume
 * (707 of 2,042 events) was a `captureException` for a case the code itself documents as
 * benign — the old Electron shell without the `getPrinters` IPC channel. Dropping it to
 * `warning` would not have removed it: a warning still opens the issue and still counts
 * events. The only thing that removes it is not emitting an event at all.
 *
 * A breadcrumb opens no issue and burns no quota, but it TRAVELS ATTACHED to the next real
 * error of the session. That is: traceability is not lost, noise is. If something genuinely
 * breaks half an hour later, the event arrives with "the IPC did not exist on this machine"
 * in its trail.
 *
 * Use it ONLY when both conditions hold:
 *   1. The failure is expected by contract (the browser forbids it, the shell is old, the user
 *      has not interacted yet), not a bug somebody has to fix.
 *   2. The user is not left broken, or the code already has an alternate path for them.
 * If the user IS left broken, this does not apply: that case is `Sentry.captureException`.
 *
 * @param {string} category stable breadcrumb grouping (e.g. 'electron.ipc', 'audio', 'pwa', 'print')
 * @param {string} message what happened, in one line and with no personal data
 * @param {Record<string, unknown>} [data] structured context; never put end-customer data here
 * @returns {void}
 */
export const noteExpectedFailure = (category: string, message: string, data?: Record<string, unknown>): void => {
  try {
    Sentry.addBreadcrumb({ category, message, level: 'info', ...(data ? { data } : {}) });
  } catch {
    // Observability must never break the flow it observes. If the SDK is not initialized
    // (tests, local build without a DSN) this is a no-op and the caller carries on.
  }
};
